<template>
  <div>
    <FormModal
      :title="createModal.title"
      :opts="opts"
      :cols="formFields"
      :opened.sync="createModal.opened"
      :value.sync="selectedItem"
      maxWidth="800px"
      @save="doSave"
    ></FormModal>

    <GModal
      v-model="listModal.opened"
      maxWidth="800px"
      :title="listModal.title"
    >
      <Table
        v-if="rubricaInUse"
        :cols="empresaCols"
        disablePagination
        :has-filter="false"
        height="auto"
        :opts="opts"
        :rows="empresas"
      ></Table>
      <div v-else>Nenhuma empresa utiliza esta rubrica.</div>
    </GModal>

    <Table
      :actionBarButtons="actionBarButtons"
      :cols="cols"
      :hasFilter="true"
      :rows="rows"
      :opts="opts"
      @contextmenu="contextmenu"
      @changePage="doLoad"
      @rowstoprint="rowstoprint"
    ></Table>
  </div>
</template>

<script>
import { BeneficiosEnum, BeneficiosLabels } from "@/core/enums/beneficios";
import { FieldTypeEnum } from "@/core/enums/field-types";
import objectToQueryParams from "@/helpers/objectToQueryParams";
import { isObject } from "lodash";

/**
 * @typedef Rubrica
 * @property {number} id
 * @property {string} nome
 * @property {string} ativo
 * @property {number} cons
 * @property {null} formula
 * @property {number} trabalho
 * @property {string} criado_em
 * @property {string} modificado_em
 * @property {number} fo_pag_templates_count
 * @property {number} FTotal
 * @property {number} FBase
 * @property {number} FHoras
 * @property {number} FDias
 * @property {number} TTotal
 * @property {number} TBase
 * @property {number} THoras
 * @property {number} TDias
 * @property {number} Subtract
 * @property {number} quantidade_empresas
 */

/**
 * @typedef FormData
 * @property {number?} id
 * @property {string | null} TBase
 * @property {string} FBase
 * @property {number} soma
 * @property {number | null} trabalho
 * @property {string} nome
 * @property {'S' | 'N'} ativo
 */

const ValorEnum = Object.freeze({
  MAIS_BASE: "TBase",
  MAIS_TOTAL: "TTotal",
  HORAS: "THoras",
  DIAS: "TDias"
});

const ReferenciaEnum = Object.freeze({
  NENHUM: "FNenhum",
  HORAS: "FHoras",
  DIAS: "FDias"
});

const OperacaoEnum = Object.freeze({
  SOMA: 0,
  SUBTRACAO: 1
});

const StatusEnum = Object.freeze({
  SIM: "S",
  NAO: "N"
});

/**
 * @function
 * @returns {FormData}
 */
const createEmptyFormData = () => ({
  id: undefined,
  TBase: null,
  FBase: ReferenciaEnum.NENHUM,
  soma: OperacaoEnum.SOMA,
  trabalho: null,
  nome: "",
  ativo: StatusEnum.SIM
});

/**
 * @function toFormData
 * @param {Rubrica} rubrica
 * @returns {FormData}
 */
const toFormData = rubrica => {
  let referencia = ReferenciaEnum.NENHUM;
  let valor = null;

  if (rubrica.FDias === 1) {
    referencia = ReferenciaEnum.DIAS;
  } else if (rubrica.FHoras === 1) {
    referencia = ReferenciaEnum.HORAS;
  }

  if (rubrica.TBase === 1) {
    valor = ValorEnum.MAIS_BASE;
  } else if (rubrica.TDias === 1) {
    valor = ValorEnum.DIAS;
  } else if (rubrica.THoras === 1) {
    valor = ValorEnum.HORAS;
  } else if (rubrica.TTotal === 1) {
    valor = ValorEnum.MAIS_TOTAL;
  }

  return {
    id: rubrica?.id || undefined,
    TBase: valor,
    FBase: referencia,
    soma:
      rubrica.Subtract === OperacaoEnum.SUBTRACAO
        ? OperacaoEnum.SUBTRACAO
        : OperacaoEnum.SOMA,
    trabalho: rubrica.trabalho || null,
    nome: rubrica.nome || "",
    ativo:
      rubrica.ativo?.toUpperCase() === StatusEnum.SIM
        ? StatusEnum.SIM
        : StatusEnum.NAO
  };
};

/**
 * @function toRubrica
 * @param {FormData} formData
 * @returns {Rubrica}
 */
const toRubrica = formData => {
  return {
    ativo: formData.ativo,
    cons: null,
    criado_em: null,
    FBase: 0,
    FDias: formData.FBase === ReferenciaEnum.DIAS ? 1 : 0,
    FHoras: formData.FBase === ReferenciaEnum.HORAS ? 1 : 0,
    fo_pag_templates_count: null,
    formula: null,
    FTotal: 0,
    id: formData.id,
    modificado_em: null,
    nome: formData.nome,
    quantidade_empresas: null,
    Subtract: formData.soma,
    TBase: formData.TBase === ValorEnum.MAIS_BASE ? 1 : 0,
    TDias: formData.TBase === ValorEnum.DIAS ? 1 : 0,
    THoras: formData.TBase === ValorEnum.HORAS ? 1 : 0,
    trabalho: formData.trabalho,
    TTotal: formData.TBase === ValorEnum.MAIS_TOTAL ? 1 : 0
  };
};

export default {
  components: {
    FormModal: () => import("@/components/form-modal.vue"),
    GModal: () => import("@/components/g-modal.vue"),
    Table: () => import("@/components/table-v.vue")
  },
  methods: {
    contextmenu(value, clickEvent) {
      const options = [
        {
          name: "Ver empresas",
          cb: async row => {
            try {
              await this.getEmpresas(row.id);
              this.listModal.title = `Empresas que usam a rubrica ${row.nome}`;
              this.listModal.opened = true;
            } catch {
              this.listModal.opened = false;
            }
          }
        },
        {
          name: "Editar",
          cb: row => {
            this.doUpdate(row);
          }
        }
      ];

      this.showContext({
        left: clickEvent.clientX,
        top: clickEvent.clientY,
        val: value,
        menu: options
      });
    },
    async doSave() {
      try {
        const body = toRubrica(this.selectedItem);
        await this.resource.save(body, body.id);
        this.doLoad();
        this.createModal.opened = false;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    doCreate() {
      this.createModal.title = "Cadastro de Rubricas Padrão";
      this.createModal.opened = true;
      this.selectedItem = createEmptyFormData();
    },
    doUpdate(row) {
      this.selectedItem = toFormData(row);
      this.createModal.title = "Edição de Rubricas Padrão";
      this.createModal.opened = true;
    },
    async doLoad(params) {
      try {
        let query = "";

        if (isObject(params?.filters)) {
          query = objectToQueryParams(params.filters, "filters");
        }

        this.rows = await this.resource.get({ query });
      } catch (error) {
        this.rows = [];

        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    async getEmpresas(rubricaId) {
      try {
        this.empresas = await this.apiResource(
          `v1/sistema/clientesTemplate/${rubricaId}`
        ).get();
      } catch (error) {
        this.empresas = [];

        if (!error.isAxiosError) {
          this.notify(error);
        }
      }
    },
    rowstoprint(resolve) {
      if (!Array.isArray(this.rows)) {
        resolve([]);
        return;
      }

      const columns = this.cols.reduce(
        (acc, col) => ({ ...acc, [col.key]: col }),
        {}
      );

      const transform = value => {
        switch (value) {
          case -1:
          case 0:
            return "Não";
          case 1:
            return "Sim";
          default:
            return value;
        }
      };

      const rows = this.rows.map(row => {
        return Object.entries(row).reduce(
          (obj, [key, value]) => ({
            ...obj,
            [key]:
              key in columns && columns[key].type === FieldTypeEnum.HTML
                ? transform(value)
                : value
          }),
          {}
        );
      });
      resolve(rows);
    }
  },
  computed: {
    formFields() {
      const valorOpts = [
        {
          id: ValorEnum.MAIS_BASE,
          name: "+ Base",
          show: true
        },
        {
          id: ValorEnum.MAIS_TOTAL,
          name: "+ Total",
          show: true
        },
        {
          id: ValorEnum.HORAS,
          name: "Horas",
          show: this.selectedItem.FBase === ReferenciaEnum.NENHUM
        },
        {
          id: ValorEnum.DIAS,
          name: "Dias",
          show: this.selectedItem.FBase === ReferenciaEnum.NENHUM
        }
      ].filter(({ show }) => show);

      return [
        {
          key: "nome",
          name: "Descrição",
          type: this.$fieldTypes.TEXT,
          colSize: 9,
          ajuda: "Descrição da rubrica no cálculo.",
          rules: [{ rule: "required" }]
        },
        {
          key: "ativo",
          name: "Ativo",
          type: this.$fieldTypes.RADIO,
          rel: { to: "ativoOpts", key: "id", name: "nome" },
          rules: [{ rule: "required" }],
          colSize: 3
        },
        {
          key: "FBase",
          name: "Aplicações referência",
          ajuda:
            "Campo da FOPAG expresso em dias, horas ou percentual. Selecione apenas uma referência para cálculo",
          type: this.$fieldTypes.RADIO,
          colSize: 6,
          rules: [{ rule: "required" }],
          rel: { to: "referenciaOpts", key: "id", name: "name" },
          valueChanged: () => {
            this.selectedItem.TBase = null;
          }
        },
        {
          key: "TBase",
          name: "Aplicações valor",
          ajuda:
            "Campo da FOPAG expresso R$.  + Base = Acima “Total de Vencimentos” no cálculo, encargos incidem sobre a rubrica. + Total = Abaixo “Total de Vencimentos” no cálculo, encargos não incidem sobre a rubrica.",
          type: this.$fieldTypes.RADIO,
          colSize: 6,
          rel: { to: valorOpts, key: "id", name: "name" },
          rules: [{ rule: "required" }]
        },
        {
          key: "soma",
          name: "A referência e/ou valor da rubrica deverá:",
          ajuda: "Comportamento da rubrica no cálculo.",
          type: this.$fieldTypes.RADIO,
          colSize: 12,
          rules: [{ rule: "required" }],
          rel: { to: "operacaoOpts", key: "id", name: "name" }
        },
        {
          key: "trabalho",
          name: "Benefício",
          ajuda:
            "Se entra no cálculo da Lei do Bem, Lei de Informática, Captação ou todos.",
          type: this.$fieldTypes.RADIO,
          rel: { to: "beneficios", key: "id", name: "nome" },
          colSize: 12,
          rules: [{ rule: "required" }]
        }
      ];
    },
    empresaCols() {
      return [
        {
          key: "empresa",
          name: "Empresa"
        },
        {
          key: "rubId",
          name: "Código FOPAG"
        },
        {
          key: "codigoFopag",
          name: "Descrição FOPAG"
        }
      ];
    },
    resource() {
      return this.apiResource(`/v1/sistema/destaques`);
    },
    rubricaInUse() {
      return Array.isArray(this.empresas) && this.empresas.length > 0;
    }
  },
  data() {
    return {
      actionBarButtons: [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          action: () => {
            this.doCreate();
          }
        }
      ],
      empresas: [],
      selectedItem: {
        soma: 1
      },
      rows: [],
      opts: {
        ativoOpts: [
          { id: StatusEnum.SIM, nome: "Sim" },
          { id: StatusEnum.NAO, nome: "Não" }
        ],
        referenciaOpts: [
          {
            id: ReferenciaEnum.NENHUM,
            name: "Nenhum"
          },
          {
            id: ReferenciaEnum.HORAS,
            name: "Horas"
          },
          {
            id: ReferenciaEnum.DIAS,
            name: "Dias"
          }
        ],
        operacaoOpts: [
          {
            name: "Somar",
            id: OperacaoEnum.SOMA
          },
          {
            name: "Subtrair",
            id: OperacaoEnum.SUBTRACAO
          }
        ],
        status: [
          {
            id: -1,
            name:
              '<i class="v-icon notranslate mdi mdi-checkbox-blank-circle-outline theme--dark" style="color: #d0d0d0"></i>'
          },
          {
            id: 0,
            name:
              '<i class="v-icon notranslate mdi mdi-checkbox-blank-circle-outline theme--dark" style="color: #d0d0d0"></i>'
          },
          {
            id: 1,
            name:
              '<i class="v-icon notranslate mdi mdi-checkbox-blank-circle theme--dark" style="color: #367be2"></i>'
          }
        ],
        beneficios: [
          {
            id: BeneficiosEnum.LEI_DO_BEM_E_INFORMATICA,
            nome: "Todos os benefícios"
          },
          { id: BeneficiosEnum.LEI_DO_BEM, nome: BeneficiosLabels.LEI_DO_BEM },
          {
            id: BeneficiosEnum.LEI_DE_INFORMATICA,
            nome: BeneficiosLabels.LEI_DE_INFORMATICA
          },
          { id: BeneficiosEnum.CAPTACAO, nome: BeneficiosLabels.CAPTACAO }
        ]
      },
      cols: [
        {
          key: "id",
          name: "ID",
          align: 1,
          type: this.$fieldTypes.TEXT
        },
        {
          key: "nome",
          name: "Nome",
          filterable: true,
          type: this.$fieldTypes.TEXT
        },
        {
          key: "quantidade_empresas",
          name: "Clientes",
          align: 1,
          type: this.$fieldTypes.TEXT
        },
        {
          key: "ativo",
          name: "Ativo",
          type: this.$fieldTypes.RADIO,
          rel: { to: "ativoOpts", key: "id", name: "nome" }
        },
        {
          key: "FHoras",
          name: "Referência como Horas",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "FDias",
          name: "Referência como Dias",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "TBase",
          name: "Valor + Base",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "TTotal",
          name: "Valor + Total",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "THoras",
          name: "Valor como Horas",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "TDias",
          name: "Valor como Dias",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "Subtract",
          name: "Subtrair",
          align: 0,
          type: this.$fieldTypes.HTML,
          rel: { to: "status", key: "id", name: "name" }
        },
        {
          key: "trabalho",
          name: "Benefício",
          rel: { to: "beneficios", key: "id", name: "nome" }
        }
      ],
      createModal: {
        title: "Cadastro de Rubricas Padrão",
        opened: false
      },
      listModal: {
        title: "",
        opened: false
      }
    };
  }
};
</script>

<style scoped lang="scss">
.modal-dialog-scrollable .modal-body::-webkit-scrollbar {
  width: 3px;
}

.overflow {
  position: absolute;
  left: 0px;
  top: 0px;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 50;
}

.kmodal {
  background: white;
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 60;
  transform: translate(-50%, -50%);
  min-width: 300px;
  min-height: 300px;
  border-radius: 5px;
  overflow: hidden;
  padding: 10px;
}

.actions {
  div {
    float: left;
    margin-right: 40px;
    cursor: pointer;
    font-weight: bold;
    color: #000000;

    img {
      margin: 5px;
    }
  }

  &:after {
    clear: both;
    content: " ";
  }
}

.filter {
  select {
    height: 33px;
    width: 100%;
  }
}
</style>
